import type { CustomDataSequence } from "building-types/dataSequences/dataSequence";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import { SHEETS_DATA_SEQUENCE_ID } from "building-types/dataSequences/systemDataSequenceTypes";
import type { Doc } from "building-types/doc";
import type { IsolatableEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { CompositeLevel } from "building-types/levels/CompositeLevel";
import type { ViewTemplate } from "building-types/views/planSets";
import type { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import { sheetGeometry3DCategories } from "building-types/views/sheetGeometryCategory";
import type { Table } from "common/data/table";
import stableHash from "common/utils/stableHash";
import type { Mutable } from "common/utils/types";
import { isLinework3DGeometryView } from "doc/viewTemplateTools";
import { selectFieldsThatAffectBuilding } from "lib/studio/docTools";
import type { PlanSetRequestPayload, ViewportPayloadBase } from "lib/workers/planSetComputeWorkerMessages";
import { selectModelSpaceAnnotation, selectContentTemplateHostedAnnotation } from "./planSetAnnotationSelectors";

/** Delimits slots so adjacent values can't run together into an ambiguous key. */
const SEP = "|";

/** Distinguishes a missing object from an empty one, which `stableHash` would otherwise conflate. */
const NULL_TOKEN = "~null";
const UNDEFINED_TOKEN = "~undef";

/**
 * Only for small payload objects: `stableHash` expands rather than digests, so a doc table would put
 * a serialization of the whole document into the key.
 */
function hashDeep(value: unknown): string {
  return stableHash(value);
}

const objectTokens = new WeakMap<object, string>();
let objectTokenCounter = 0;

/**
 * The doc is immutable, so a reference-equal table is value-equal; hashing contents would traverse
 * the whole document to reach the answer identity already gives.
 */
function refToken(value: unknown): string {
  if (value === null) {
    return NULL_TOKEN;
  }
  if (value === undefined) {
    return UNDEFINED_TOKEN;
  }
  if (typeof value !== "object") {
    return stableHash(value);
  }
  const existing = objectTokens.get(value as object);
  if (existing !== undefined) {
    return existing;
  }
  const token = `@${++objectTokenCounter}`;
  objectTokens.set(value as object, token);
  return token;
}

/**
 * Mirrors `shallowCompare`, including that excluded keys still contribute their presence — it counts
 * every own property before skipping the ignored ones.
 */
function hashShallowByRef(obj: object | null | undefined, exclude?: readonly string[]): string {
  if (obj === null) {
    return NULL_TOKEN;
  }
  if (obj === undefined) {
    return UNDEFINED_TOKEN;
  }
  const record = obj as Record<string, unknown>;
  let result = "{";
  for (const key of Object.getOwnPropertyNames(obj).sort()) {
    result += exclude?.includes(key) ? `${key}:~skip,` : `${key}:${refToken(record[key])},`;
  }
  return result + "}";
}

function hashIsolatedEntity(isolatedEntity?: IsolatableEntityHandle): string {
  return `${isolatedEntity?.type ?? ""}:${isolatedEntity?.id ?? ""}`;
}

const docBuildingHashes = new WeakMap<Doc, string>();
const docContentTemplateAnnotationHashes = new WeakMap<Doc, string>();

/** Memoized per `Doc`, so the annotation table is filtered once per version, not once per lookup. */
export function hashDocBuildingAndModelSpaceAnnotation(doc: Doc): string {
  const cached = docBuildingHashes.get(doc);
  if (cached !== undefined) {
    return cached;
  }

  // Annotation goes through the model space filter below instead, so editing annotation hosted on a
  // sheet template doesn't invalidate every viewport.
  const fields = selectFieldsThatAffectBuilding(doc) as Mutable<Partial<Doc>>;
  delete fields.annotation;

  const result = [
    hashShallowByRef(fields),
    hashShallowByRef(doc.globalParameters),
    hashShallowByRef(doc.sectionViews),
    hashShallowByRef(doc.viewports),
    hashShallowByRef(selectModelSpaceAnnotation(doc)),
  ].join(SEP);

  docBuildingHashes.set(doc, result);
  return result;
}

export function hashDocContentTemplateHostedAnnotation(doc: Doc): string {
  const cached = docContentTemplateAnnotationHashes.get(doc);
  if (cached !== undefined) {
    return cached;
  }
  const result = hashShallowByRef(selectContentTemplateHostedAnnotation(doc));
  docContentTemplateAnnotationHashes.set(doc, result);
  return result;
}

/**
 * 3D geometry is returned in full and filtered outside the compute service, so templates differing
 * only in their 3D categories can share a result.
 */
function nonLinework3DCategories(viewTemplate: ViewTemplate): SheetGeometryCategory[] {
  return viewTemplate.categories.filter((category) => !sheetGeometry3DCategories.includes(category)).sort();
}

function hashCategories(viewTemplate: ViewTemplate): string {
  if (!isLinework3DGeometryView(viewTemplate.viewType)) {
    return hashDeep(viewTemplate.categories);
  }
  return hashDeep(nonLinework3DCategories(viewTemplate));
}

function hashStyleOverrides(viewTemplate: ViewTemplate): string {
  const { styleOverrides } = viewTemplate;
  if (!isLinework3DGeometryView(viewTemplate.viewType) || !styleOverrides) {
    return hashDeep(styleOverrides);
  }

  // The old comparator inspected only its first operand's non-3D categories, which reads as
  // asymmetric but isn't: the category check has already proved both operands carry the same ones.
  const restricted: Record<string, unknown> = {};
  for (const category of nonLinework3DCategories(viewTemplate)) {
    restricted[category] = styleOverrides[category];
  }
  return hashDeep(restricted);
}

/** Reduced to the compared fields; hashing raw levels would over-invalidate on ones it ignored. */
function hashCompositeLevels(compositeLevels: Table<CompositeLevel> | undefined): string {
  if (!compositeLevels) {
    return UNDEFINED_TOKEN;
  }
  const canonical = Object.keys(compositeLevels)
    .sort()
    .map((key) => {
      const level = compositeLevels[key];
      return [
        level.id,
        level.name,
        level.sortIndex,
        level.members.map((member) => [member.entityId, hashDeep(member.componentInstanceChain)]),
      ];
    });
  return hashDeep(canonical);
}

function viewportDependencies(payload: ViewportPayloadBase, doc: Doc): string[] {
  return [
    hashDocBuildingAndModelSpaceAnnotation(doc),
    hashShallowByRef(payload.optionChoices),
    hashShallowByRef(payload.viewTemplate, ["categories", "styleOverrides"]),
    hashCategories(payload.viewTemplate),
    hashStyleOverrides(payload.viewTemplate),
    hashShallowByRef(doc.viewTemplateOverrides),
    hashShallowByRef(doc.effects),
    hashShallowByRef(doc.ruleBasedSelectors),
    hashShallowByRef(doc.ruleBasedSelectorInstances),
    hashCompositeLevels(doc.compositeLevels),
    hashDeep(payload.contentSourceId),
    hashDeep(payload.selectedReportingUnitLabelId),
    hashDeep(payload.dimensionPrecision),
    // Changes the emitted geometry but was never compared before, so two requests differing only in
    // render style could serve each other's output.
    hashDeep(payload.renderStyleMode),
  ];
}

function tableContentsDependencies(
  payload: Extract<PlanSetRequestPayload, { type: "GET_TABLE_CONTENTS" }>,
  doc: Doc,
): string[] {
  const { dataTable, pricingContext } = payload;
  const dependencies = [
    hashDocBuildingAndModelSpaceAnnotation(doc),
    hashDeep(dataTable.dataSequenceId),
    hashDeep(dataTable.width),
    hashDeep(dataTable.fontSize),
    hashDeep(dataTable.presetId),
    hashDeep(dataTable.levelId),
    hashDeep(dataTable.selectedReportingUnitLabelId),
    hashDeep(dataTable.sortColumnIdOrder),
    hashDeep(dataTable.sortColumnId),
    hashDeep(dataTable.titleHidden),
    hashDeep(dataTable.columnHeadersHidden),
    hashDeep(dataTable.title),
    hashDeep(dataTable.titleFontSize),
    hashDeep(dataTable.titleAlignment),
    hashDeep(dataTable.firstRow),
    hashDeep(dataTable.maxRowCount),
    hashShallowByRef(dataTable.optionSetOverrides),
    hashDeep(dataTable.columnConfig),
    hashDeep(dataTable.findAndReplace),
    hashDeep(pricingContext),
  ];

  // By value, not table reference: option edits rewrite the containing tables without changing what
  // actually renders.
  const buildingOptionDataSequenceId: SystemDataSequenceEntityType = "BUILDING_OPTION";
  if (dataTable.dataSequenceId === buildingOptionDataSequenceId) {
    dependencies.push(hashDeep(doc.optionSets));
  }

  // TODO: handle sheet list data sequences -- needs the pages be passed through the msg
  const sequence =
    dataTable.dataSequenceId !== SHEETS_DATA_SEQUENCE_ID ? doc.dataSequences[dataTable.dataSequenceId] : undefined;
  if (sequence) {
    // Custom sequences can hold thousands of rows, and the old comparator used `===` on them.
    dependencies.push(refToken((sequence as CustomDataSequence).rows));
  }

  return dependencies;
}

/**
 * Every dependency that can change the output must appear here — a missing one silently serves a
 * stale result. The switch is exhaustive, so a new job type is a compile error.
 */
export function planSetCacheKey(msg: PlanSetRequestPayload, doc: Doc, isolatedEntity?: IsolatableEntityHandle): string {
  const prefix = [msg.type, hashIsolatedEntity(isolatedEntity)];

  switch (msg.type) {
    case "GET_TABLE_CONTENTS":
      return [...prefix, ...tableContentsDependencies(msg, doc)].join(SEP);

    case "RENDER_DRAWING_VIEWPORT":
      // Renders content template geometry, not the building, so it depends on the template
      // annotation rather than on the building fields.
      return [
        ...prefix,
        hashDeep(msg.viewport),
        hashDeep(msg.viewTemplate),
        hashShallowByRef(msg.globalParameterValues, ["currentTime"]),
        hashShallowByRef(doc.viewTemplateOverrides),
        hashDocContentTemplateHostedAnnotation(doc),
      ].join(SEP);

    case "RENDER_PLAN":
      return [
        ...prefix,
        ...viewportDependencies(msg, doc),
        hashDeep(msg.viewport),
        hashShallowByRef(msg.debugPrefs),
        hashShallowByRef(msg.globalParameterValues, ["currentTime"]),
        hashDeep(msg.planSetTemplateId),
        hashDeep(msg.planSetTemplateConfigId),
        hashShallowByRef(doc.planSetTemplateConfigs),
        hashShallowByRef(doc.localParameters),
      ].join(SEP);

    case "RENDER_PERSPECTIVE_DRAWING":
      // `sceneOptions` is a fresh wrapper per render, so hash its contents rather than its identity,
      // as RENDER_ORTHOGRAPHIC_DRAWING below does. `visibilitySelectors` is excluded for the same
      // reason as there: the compute service replaces it with the built-in perspective selectors.
      return [
        ...prefix,
        ...viewportDependencies(msg, doc),
        hashShallowByRef(msg.perspectiveDrawing, ["sceneOptions"]),
        hashShallowByRef(msg.perspectiveDrawing.sceneOptions, ["visibilitySelectors"]),
      ].join(SEP);

    case "RENDER_ORTHOGRAPHIC_DRAWING":
      return [
        ...prefix,
        ...viewportDependencies(msg, doc),
        hashShallowByRef(msg.drawing, ["sceneOptions"]),
        hashShallowByRef(msg.drawing.sceneOptions, ["visibilitySelectors"]),
      ].join(SEP);

    case "RENDER_SECTION_INTERSECTIONS":
    case "GET_PERSPECTIVE_VIEW_GEOMETRY":
    case "GET_ORTHOGRAPHIC_VIEW_GEOMETRY":
      return [...prefix, ...viewportDependencies(msg, doc)].join(SEP);
  }
}
